import { cloneDeep, get, set, groupBy, mapValues, keyBy, isPlainObject, omitBy, isNil } from 'lodash';

import serverErrors from './json-api/server-errors';

function getIncluded(included, { type, id }) {
  if (included[type] && included[type][id]) return included[type][id].attributes;
  return undefined;
}

export function deattribute({ attributes, relationships = {} }, included) {
  return ({
    ...attributes,
    ...Object.keys(relationships).reduce((h, attr) => {
      const { data: resources } = relationships[attr];
      if (Array.isArray(resources)) {
        // eslint-disable-next-line no-param-reassign
        h[attr] = resources.map(resource => getIncluded(included, resource));
      } else if (resources) {
        h[attr] = getIncluded(included, resources); // eslint-disable-line no-param-reassign
      } else {
        h[attr] = null; // eslint-disable-line no-param-reassign
      }
      return h;
    }, {}),
  });
}

export function deserialize({ data, included, ...rest }) {
  let includedGrouped = groupBy(included, 'type');
  includedGrouped = mapValues(includedGrouped, value => keyBy(value, 'id'));

  if (Array.isArray(data)) {
    const all = data.map(resource => deattribute(resource, includedGrouped));
    return { data: all, included: includedGrouped, ...rest };
  }
  const one = deattribute(data, includedGrouped);
  return { data: one, included: includedGrouped, ...rest };
}

export function resourceDeserializer(data, attributes) {
  const deserialized = cloneDeep(data);
  attributes.forEach((config) => {
    const { prop, deserialize: deserializeFn } = config;
    if (deserializeFn) {
      set(deserialized, prop, deserializeFn(get(data, prop), { config, data }));
    }
  });
  return deserialized;
}

export function errorsDeserializer(errors, attributes) {
  const deserialized = cloneDeep(errors);
  attributes.forEach((config) => {
    const { key, prop } = config;

    if (!errors[prop]) return;

    if (prop !== key) {
      delete deserialized[prop];
      deserialized[key] = errors[prop];
    }
  });
  Object.keys(deserialized).forEach((attr) => {
    deserialized[attr] = deserialized[attr].map(({ error, ...data }) => {
      const {
        name = error,
        params = () => [],
        options = { hasTarget: false, immediate: true },
      } = serverErrors[error] || {};
      return {
        name,
        params: params(data),
        options,
      };
    });
  });
  return deserialized;
}

export function resourceErrorsDeserializer(data, attributes) {
  const { errors, resource } = data;
  return {
    ...data,
    errors: errorsDeserializer(errors, attributes),
    resource: resource && resourceDeserializer(resource, attributes),
  };
}

export function resourceSerializer(data, attributes) {
  const serialized = { ...data };
  attributes.forEach((config) => {
    const { prop, serialize: serializeFn } = config;
    if (serializeFn) {
      delete serialized[prop];
      Object.assign(serialized, serializeFn(data[prop], { config, data }));
    }
  });
  return serialized;
}

export function paginationSerializer({ page, per }) {
  return omitBy({
    _page: page,
    _limit: per,
  }, isNil);
}

export function filterSerializer(key, value) {
  const [filterKey, data] = (isPlainObject(value) && value.op) ?
    [`${key}_${value.op}`, value.query]
    : [key, value];

  if (isNil(data) || data === '') return {};

  if (Array.isArray(data)) {
    return { [filterKey]: data };
  }

  return { [filterKey]: data };
}

export function resourceReponseDeserializer(response, attributes) {
  const { data: responseData, ...responseOther } = response;
  if (!responseData) return response;
  const { data, ...responseDataOther } = deserialize(responseData);
  if (Array.isArray(data)) {
    const all = data.map(resource => resourceDeserializer(resource, attributes));
    return { data: { data: all, ...responseDataOther }, ...responseOther };
  }
  const one = resourceDeserializer(data, attributes);
  return { data: { data: one, ...responseDataOther }, ...responseOther };
}

export function resourceErrorsReponseDeserializer(error, attributes) {
  const { response = {}, ...errorOther } = error;
  const { status } = response;
  if (status === 422) {
    const { data: responseData, ...responseOther } = response;
    if (!responseData) return error;
    const data = resourceErrorsDeserializer(responseData, attributes);
    return { response: { data, ...responseOther }, ...errorOther };
  }
  return error;
}
